




































































































































import type { PropType, Ref } from '@vue/composition-api';
import {
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  computed,
  defineComponent,
} from '@vue/composition-api';
import type { EBlockWidth, Row, MergeValues } from '@getaccept/editor-lib';
import {
  useObserveElement,
  NodeType,
  RowType,
  EditorHelper,
  Context,
  ContractHelper,
} from '@getaccept/editor-lib';
import type { Recipient } from '@getaccept/lib-shared/src/types/Recipient';
import type { User } from '@getaccept/lib-shared/src/users/types/user';
import type { EditorRecipientInput } from '@getaccept/lib-shared/src/types/editor-recipient-input';
import type { SigningTheme } from '@getaccept/lib-shared/src/signing-theme/types/theme';
import type { PartiallySignedEvent } from '@getaccept/editor-lib/src/types/signed-field';
import type { PusherClient } from '@getaccept/pusher';
import type { Comment } from '@getaccept/lib-shared/src/contextual-commenting/types/comment';
import { CommentsHelper } from '@getaccept/lib-shared/src/contextual-commenting/helpers/comments.helper';
import { CreateContextualCommentButtonState } from '@getaccept/lib-shared/src/contextual-commenting/types/create-contextual-comment-button-state';
import DetachedCommentAnchor from '@getaccept/lib-shared/src/contextual-commenting/components/DetachedCommentAnchor.vue';
import EditorSideBySideRow from './EditorSideBySideRow.vue';
import EditorRow from './EditorRow.vue';
import EditorRowColumns from './EditorRowColumns.vue';
import EditorRowSlideShow from './EditorRowSlideShow.vue';

export default defineComponent({
  name: 'EditorSection',
  components: {
    DetachedCommentAnchor,
    EditorRow,
    EditorRowColumns,
    EditorSideBySideRow,
    EditorRowSlideShow,
  },
  props: {
    context: { type: String as PropType<Context>, default: () => Context.Document },
    focusedRequiredId: { type: String, default: '' },
    sectionId: { type: String, required: true },
    rows: { type: Array as PropType<Row[]>, default: () => [] },
    mergeValues: {
      type: Object as PropType<MergeValues>,
      default: () => ({ document: {}, sender: {}, recipients: [], entity: {} }),
    },
    isPdfViewer: { type: Boolean },
    isDsr: { type: Boolean },
    isPreview: { type: Boolean },
    isHidden: { type: Boolean },
    blockWidthClass: {
      type: String as PropType<EBlockWidth>,
      default: null,
    },
    pageWidth: { type: Number, default: 0 },
    isBlockToggled: { type: Boolean },
    isSigningSite: { type: Boolean },
    sender: {
      type: Object as PropType<User>,
      default: () => ({}),
    },
    theme: {
      type: Object as PropType<SigningTheme>,
      default: () => ({}),
    },
    recipient: {
      type: Object as PropType<Recipient>,
      default: () => ({}),
    },
    recipientInput: { type: Array as PropType<EditorRecipientInput[]>, required: true },
    updatingInputs: { type: Array as PropType<EditorRecipientInput[]>, required: true },
    failedInputUpdates: { type: Array as PropType<EditorRecipientInput[]>, required: true },
    partiallySignedFields: { type: Array as PropType<PartiallySignedEvent[]>, default: () => [] },
    pusherClient: { type: Object as PropType<PusherClient>, default: undefined },
    comments: { type: Array as PropType<Comment[]>, default: () => [] },
    selectedCommentId: { type: String, default: '' },
    showContextualComments: { type: Boolean },
    createContextualCommentButtonState: {
      type: String as PropType<CreateContextualCommentButtonState>,
      default: CreateContextualCommentButtonState.Enabled,
    },
  },
  setup(props, { emit }) {
    const filteredRows = computed(() =>
      ContractHelper.filterRowsByContractLinkAccess(props.rows, props.recipient)
    );

    const sectionContainer: Ref<HTMLElement> = ref(null);

    const { observe, disconnect } = useObserveElement(emit);

    const initializeIntersectionObserver = async () => {
      await nextTick();
      if (!sectionContainer.value) {
        return;
      }
      observe(sectionContainer.value);
    };

    const rowIds = computed(() => props.rows.map(row => row.id));

    const commentsWithoutRowId = computed(() =>
      props.comments.filter(comment => !rowIds.value.includes(comment.editorSelection?.rowId))
    );

    onMounted(() => {
      if (!props.isDsr) {
        return;
      }
      initializeIntersectionObserver();
    });

    onBeforeUnmount(() => {
      if (!props.isDsr) {
        return;
      }
      disconnect();
    });

    return {
      commentsWithoutRowId,
      filterCommentsByRow: CommentsHelper.filterCommentsByRow,
      filteredRows,
      initializeIntersectionObserver,
      isPreviousRowSideBySide: EditorHelper.isPreviousRowSideBySide,
      NodeType,
      rowContainsCoverSize: EditorHelper.rowContainsCoverSize,
      RowType,
      sectionContainer,
    };
  },
});
